import styled from "styled-components";
import { Link } from "react-router-dom";

export const AuthPage = styled.main<{ $dimmed?: boolean }>`
  min-height: 100vh;
  background: ${({ $dimmed }) => ($dimmed ? "#8d969b" : "#f8f8f8")};
  color: #181818;
  display: flex;
  flex-direction: column;
  position: relative;
`;

export const Header = styled.header`
  height: 72px;
  padding: 0 28px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
`;

export const BrandGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

export const BrandPrefix = styled.span`
  color: #5a5959;
  font-size: 13px;
  line-height: 18px;
`;

export const BrandName = styled.span`
  color: #181818;
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  letter-spacing: 0;
  text-transform: uppercase;
`;

export const CenterBrand = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export const LegacyLogo = styled.img`
  width: 170px;
  height: auto;
`;

export const LdaLogo = styled.img`
  width: 65px;
  height: auto;
`;

export const DemoBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 22px;
  border: 1px solid #065fa6;
  border-radius: 999px;
  color: #065fa6;
  font-size: 12px;
  line-height: 16px;
  padding: 0 10px;
`;

export const Content = styled.section`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 30px 16px 96px;
`;

export const Card = styled.div<{ $size?: "default" | "compact" }>`
  width: ${({ $size }) => ($size === "compact" ? "400px" : "464px")};
  max-width: calc(100vw - 32px);
  background: #ffffff;
  border: 0;
  border-radius: 8px;
  padding: 40px;
  box-shadow: 0 1px 2px rgba(24, 24, 24, 0.04);
`;

export const Title = styled.h1`
  font-size: 28px;
  line-height: 36px;
  font-weight: 700;
  color: #181818;
  text-align: center;
  margin-bottom: 32px;
`;

export const CardIcon = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
`;

export const Form = styled.form`
  display: grid;
  gap: 24px;
`;

export const ActionStack = styled.div`
  display: grid;
  gap: 16px;
`;

export const BodyText = styled.p`
  color: #3f3f3f;
  font-size: 16px;
  line-height: 24px;
  margin-bottom: 32px;
  text-align: center;
`;

export const SupportText = styled.p`
  color: #181818;
  font-size: 16px;
  line-height: 24px;
  margin: 0 0 16px;
`;

export const SupportLink = styled.a`
  color: #065fa6;
  font-weight: 600;
`;

export const Field = styled.label`
  display: grid;
  gap: 8px;
  color: #181818;
  font-size: 16px;
  line-height: 20px;
`;

export const InputWrap = styled.div`
  position: relative;
`;

export const Input = styled.input`
  width: 100%;
  height: 44px;
  border: 1px solid #d7d7d7;
  border-radius: 8px;
  background: #ffffff;
  color: #181818;
  font-size: 16px;
  line-height: 20px;
  padding: 0 40px 0 14px;

  &:focus {
    border-color: #065fa6;
    outline: 0;
  }
`;

export const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  border: 0;
  background: transparent;
  color: #5a5959;
  cursor: pointer;
  font-size: 18px;
  line-height: 18px;
  padding: 4px;
`;

export const CheckboxLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  color: #181818;
  font-size: 16px;
  line-height: 24px;

  input {
    accent-color: #065fa6;
    height: 20px;
    width: 20px;
    border-radius: 4px;
  }
`;

export const Button = styled.button`
  height: 48px;
  border: 0;
  border-radius: 8px;
  background: #065fa6;
  color: #ffffff;
  cursor: pointer;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;

  &:disabled {
    background: #d0d1d0;
    cursor: not-allowed;
  }
`;

export const ButtonLink = styled(Link)`
  height: 48px;
  border-radius: 8px;
  background: #065fa6;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 20px;
  text-decoration: none;
`;

export const TextButton = styled.button`
  border: 0;
  background: transparent;
  color: #181818;
  cursor: pointer;
  font-size: 16px;
  line-height: 20px;
  text-decoration: underline;

  &:hover {
    color: #065fa6;
  }
`;

export const LinkRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 0;

  a {
    color: #181818;
    font-size: 16px;
    line-height: 20px;
    text-decoration: underline;

    &:hover {
      color: #065fa6;
    }
  }
`;

export const Alert = styled.div<{ $tone?: "error" | "success" }>`
  width: 464px;
  max-width: calc(100vw - 32px);
  margin: 0 auto 24px;
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  align-items: start;
  border: 1px solid
    ${({ $tone }) => ($tone === "success" ? "#06797f" : "#e5a7a7")};
  background: ${({ $tone }) => ($tone === "success" ? "#eaf7f5" : "#fdecec")};
  border-radius: 8px;
  color: ${({ $tone }) => ($tone === "success" ? "#005061" : "#850000")};
  font-size: 16px;
  line-height: 24px;
  padding: 14px 16px;
`;

export const AlertIcon = styled.span<{ $tone?: "error" | "success" }>`
  color: ${({ $tone }) => ($tone === "success" ? "#06797f" : "#c40000")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 24px;
  font-size: 14px;
  line-height: 24px;
`;

export const AlertContent = styled.div`
  display: grid;
  gap: 4px;
`;

export const AlertAction = styled.a`
  color: #181818;
  font-weight: 700;
  text-decoration: underline;
`;

export const AlertClose = styled.button`
  border: 0;
  background: transparent;
  color: #5a5959;
  cursor: pointer;
  font-size: 18px;
  line-height: 18px;
  padding: 0;
`;

export const FieldError = styled.span`
  color: #850000;
  font-size: 14px;
  line-height: 18px;
`;

export const RequirementList = styled.ul`
  list-style: none;
  display: grid;
  gap: 12px;
  margin: 0;
`;

export const RequirementItem = styled.li<{
  $valid: boolean;
  $invalid?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${({ $valid, $invalid }) =>
    $valid ? "#181818" : $invalid ? "#850000" : "#5a5959"};
  font-size: 16px;
  line-height: 20px;

  &::before {
    content: "${({ $valid }) => ($valid ? "✓" : "")}";
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    flex-shrink: 0;
    font-size: 12px;
    color: #ffffff;
    background: ${({ $valid }) => ($valid ? "#06797f" : "transparent")};
    border: 1px solid
      ${({ $valid, $invalid }) =>
        $valid ? "#06797f" : $invalid ? "#850000" : "#d0d1d0"};
    box-sizing: border-box;
  }
`;

export const Footer = styled.footer`
  text-align: center;
  color: #5a5959;
  font-size: 14px;
  line-height: 20px;
  padding: 0 16px;
  position: absolute;
  bottom: 28px;
  left: 0;
  width: 100%;

  a {
    color: #181818;
    font-weight: 600;
    text-decoration: underline;

    &:hover {
      color: #065fa6;
    }
  }
`;
